import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4s4l291e6in3mfvkb80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="bg-navy rounded-3xl overflow-hidden mx-5">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-12 lg:p-20 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Let's Discuss Your AI Strategy
                </h2>
                <p className="text-gray-300 text-lg mb-12">
                  Schedule a consultation to explore how strategic AI guidance can accelerate your business transformation.
                </p>

                <div className="space-y-6">
                  <div className="flex items-center gap-4 text-white">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/20 rounded-lg">
                      <i className="ri-mail-line text-xl text-primary"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email Us</p>
                      <p className="font-medium">thesavaigroup@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-white">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-phone-line text-xl text-secondary"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Call Us</p>
                      <p className="font-medium">(919) 672-9819</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-white">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent/20 rounded-lg">
                      <i className="ri-time-line text-xl text-accent"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Business Hours</p>
                      <p className="font-medium">Mon - Fri: 9AM - 6PM EST</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-12 border-t border-white/10">
                  <p className="text-gray-400 text-sm mb-4">Connect With Us</p>
                  <div className="flex gap-4">
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg hover:bg-primary transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill text-white"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg hover:bg-primary transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill text-white"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg hover:bg-primary transition-colors cursor-pointer">
                      <i className="ri-youtube-fill text-white"></i>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="bg-white p-12 lg:p-16">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h3 className="text-2xl font-bold text-charcoal mb-2">Request a Consultation</h3>
                <p className="text-gray-600 mb-8">We'll respond within 24 hours to schedule our initial discussion</p>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="john@company.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-charcoal mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="Your Company"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-charcoal mb-2">
                        What challenges are you facing? *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-none"
                        placeholder="Describe your current situation and what you'd like to achieve..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white py-4 rounded-lg font-medium hover:bg-primary/90 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? 'Sending...' : 'Request Consultation'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                        Thank you! We'll be in touch soon.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                        Something went wrong. Please try again.
                      </div>
                    )}
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}